import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { affiliatesApi, organizationsApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface OrgDetails {
  organizationId: string;
  name: string;
  slug: string;
  logo?: string;
}

interface CommissionDetails {
  name: string;
  type: string;
  commissionValue: number;
}

export default function JoinOrg() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading: authLoading } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [orgDetails, setOrgDetails] = useState<OrgDetails | null>(null);
  const [commissionDetails, setCommissionDetails] = useState<CommissionDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [joinStatus, setJoinStatus] = useState<'approved' | 'pending' | null>(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [promotionPlan, setPromotionPlan] = useState('');

  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!orgSlug) {
        setError('Invalid organization link');
        setIsLoading(false);
        return;
      }

      // Wait for auth to be determined before checking authentication
      if (authLoading) {
        return;
      }

      // If not authenticated, redirect to login
      if (!isAuthenticated) {
        navigate(`/login?redirect=/join/${orgSlug}`);
        return;
      }

      // If not an affiliate user, show error
      if (user?.role !== 'affiliate') {
        setError('Only affiliate accounts can join organization programs.');
        setIsLoading(false);
        return;
      }

      try {
        // Fetch organization details
        const response = await organizationsApi.getBySlug(orgSlug);
        if (response.data.success) {
          const { organization, defaultCampaign } = response.data.data;
          setOrgDetails({
            organizationId: organization.organizationId,
            name: organization.name,
            slug: organization.slug,
            logo: organization.logo,
          });
          if (defaultCampaign) {
            setCommissionDetails({
              name: defaultCampaign.name,
              type: defaultCampaign.type,
              commissionValue: defaultCampaign.commissionValue,
            });
          }
          // Show the application form instead of auto-joining
          setShowApplicationForm(true);
        }
      } catch (err: unknown) {
        const error = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
        if (error.response?.status === 404) {
          setError('This organization was not found.');
        } else {
          setError('Failed to load organization details.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgDetails();
  }, [orgSlug, isAuthenticated, user, navigate, authLoading]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!promotionPlan.trim()) {
      toast.error('Please describe how you plan to promote this product');
      return;
    }

    setIsJoining(true);
    try {
      const joinResponse = await affiliatesApi.join(orgSlug!, promotionPlan.trim());
      if (joinResponse.data.success) {
        const status = joinResponse.data.data.status;
        setJoinStatus(status === 'approved' ? 'approved' : 'pending');
        setShowApplicationForm(false);
      }
    } catch (joinErr: unknown) {
      const err = joinErr as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (err.response?.status === 409) {
        // Already an affiliate
        toast.success('You are already part of this affiliate program!');
        navigate('/dashboard');
        return;
      }
      toast.error(err.response?.data?.error?.message || 'Failed to join organization');
    } finally {
      setIsJoining(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust mx-auto mb-4"></div>
          <p className="text-charcoal/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-8 h-8 text-rust" />
          </div>
          <h1 className="text-2xl font-display font-bold text-charcoal mb-2">Unable to Join</h1>
          <p className="text-charcoal/60 mb-6">{error}</p>
          <Link to="/" className="btn btn-primary">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (joinStatus === 'approved') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-sage" />
          </div>
          <h1 className="text-2xl font-display font-bold text-charcoal mb-2">Welcome Aboard!</h1>
          <p className="text-charcoal/60 mb-6">
            You are now an affiliate for <strong>{orgDetails?.name}</strong>.
            Start creating referral links and earning commissions!
          </p>
          <div className="space-y-3">
            <Link to="/links" className="btn btn-primary w-full">
              Create Referral Links
            </Link>
            <Link to="/dashboard" className="btn btn-secondary w-full">
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (joinStatus === 'pending') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ClockIcon className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-display font-bold text-charcoal mb-2">Application Submitted!</h1>
          <p className="text-charcoal/60 mb-4">
            Your application to join <strong>{orgDetails?.name}</strong>&apos;s affiliate program has been submitted.
          </p>
          <p className="text-charcoal/60 mb-6 text-sm">
            The organization will review your application and you&apos;ll receive an email once you&apos;re approved.
            This typically takes 1-2 business days.
          </p>
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Show application form
  if (showApplicationForm && orgDetails) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-warm-white rounded-xl shadow-lg p-8">
          {/* Org Header */}
          <div className="text-center mb-6">
            {orgDetails.logo ? (
              <img
                src={orgDetails.logo}
                alt={orgDetails.name}
                className="w-16 h-16 rounded-full mx-auto mb-4 object-cover bg-white"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-rust flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">
                  {orgDetails.name.charAt(0)}
                </span>
              </div>
            )}
            <h1 className="text-2xl font-display font-bold text-charcoal mb-2">
              Join {orgDetails.name}
            </h1>
            <p className="text-charcoal/60">
              Apply to become an affiliate partner
            </p>
          </div>

          {/* Commission Info */}
          {commissionDetails && (
            <div className="bg-sage/10 border border-sage/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-charcoal/70">Commission</span>
                <span className="font-semibold text-sage">
                  {commissionDetails.type === 'percentage'
                    ? `${commissionDetails.commissionValue}%`
                    : `$${(commissionDetails.commissionValue / 100).toFixed(2)}`}
                </span>
              </div>
            </div>
          )}

          {/* Application Form */}
          <form onSubmit={handleJoin} className="space-y-5">
            <div>
              <label htmlFor="promotionPlan" className="label">
                How do you plan to promote {orgDetails.name}? *
              </label>
              <textarea
                id="promotionPlan"
                value={promotionPlan}
                onChange={(e) => setPromotionPlan(e.target.value)}
                rows={4}
                className="input resize-none"
                placeholder="Describe your audience, marketing channels, and promotion strategy (e.g., blog, social media, email list, YouTube channel, etc.)"
                required
              />
              <p className="text-xs text-charcoal/50 mt-1">
                This helps the organization understand if you&apos;re a good fit for their affiliate program.
              </p>
            </div>

            <button
              type="submit"
              disabled={isJoining || !promotionPlan.trim()}
              className="btn btn-primary w-full py-3"
            >
              {isJoining ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting Application...
                </span>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-charcoal/50">
            By submitting, you agree to follow {orgDetails.name}&apos;s affiliate terms and guidelines.
          </p>
        </div>
      </div>
    );
  }

  // Fallback - should not normally reach here
  // This handles edge cases where state hasn't fully loaded
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust mx-auto mb-4"></div>
        <p className="text-charcoal/60">Loading organization...</p>
      </div>
    </div>
  );
}
